import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDrag } from '@use-gesture/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';
import { bookDownloadDetail, bookDownloadLabel, useBookDownload } from '@/hooks/useBookDownload';
import { SLEEP_TIMER_PRESETS_MINUTES, type SleepTimerState } from '@/hooks/useSleepTimer';
import { DOWNLOAD_REFUSED_MESSAGE } from '@/native/downloadPreflight';
import type { ReaderBook } from '@/services/booksApi';
import type { Book } from '@/utils/bookRouting';
import { ChevronIcon } from './icons/ChevronIcon';
import { ClockIcon } from './icons/ClockIcon';
import { CloseIcon } from './icons/CloseIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { TickIcon } from './icons/TickIcon';

/**
 * The player's "more" sheet: the bottom panel growing upward to show the
 * options that do not earn a permanent button — the sleep timer, and
 * downloading the book.
 *
 * It sits directly on top of the transport controls and shares their
 * background, so open it reads as one panel that got taller, not as a
 * popup over the player. The rest of the screen is dimmed by
 * `PlayerMoreScrim`.
 *
 * Two kinds of row. The sleep timer is a choice among many, so its row
 * leads to a second view inside the same sheet. The download is a single
 * action with a state, so everything it has to say — its size, its
 * progress, the refusal when the book cannot fit, the confirmation before
 * removing — happens in the row's own place, never in a popup.
 */

export const REMOVE_DOWNLOAD_COPY =
  "This removes the downloaded book from this phone. The part you're listening to fills back up as you listen.";

/** The sleep timer's state as the short figure on its row. */
export const sleepTimerRowValue = (sleepTimer: SleepTimerState | null, remainingSeconds: number | null): string => {
  if (!sleepTimer) {
    return 'Off';
  }
  if (sleepTimer.mode === 'end-of-chapter') {
    return 'End of chapter';
  }
  if (remainingSeconds === null) {
    return 'On';
  }
  return remainingSeconds >= 60
    ? `${Math.ceil(remainingSeconds / 60)} min left`
    : `0:${String(remainingSeconds).padStart(2, '0')} left`;
};

interface Palette {
  surface: string;
  text: string;
  muted: string;
  accent: string;
  hairline: string;
  pressed: string;
  grabber: string;
  /** In the dark the shadow cannot be seen, so the top edge is drawn instead. */
  edge: string;
  secondaryButton: string;
  dangerButton: string;
  option: (active: boolean) => string;
}

const usePalette = (): Palette => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const button = 'px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border shrink-0 disabled:opacity-50 disabled:cursor-default';
  return {
    surface: dark ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)',
    text: dark ? 'text-gray-200' : 'text-gray-800',
    muted: dark ? 'text-gray-400' : 'text-gray-500',
    accent: dark ? 'text-gray-200' : 'text-blue-700',
    hairline: dark ? 'border-zinc-700' : 'border-gray-200',
    pressed: dark ? 'active:bg-zinc-700/60' : 'active:bg-gray-200/70',
    grabber: dark ? 'bg-zinc-600' : 'bg-gray-300',
    edge: dark ? 'border-t border-white/10' : '',
    secondaryButton: `${button} ${dark ? 'border-zinc-600 text-gray-200 hover:bg-zinc-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`,
    dangerButton: `${button} ${dark ? 'border-red-800 text-red-400 hover:bg-red-900/20' : 'border-red-200 text-red-600 hover:bg-red-50'}`,
    option: (active) =>
      `cursor-pointer rounded-lg px-3 py-3 text-sm transition-colors ${
        active
          ? dark
            ? 'bg-zinc-700 font-semibold text-gray-100'
            : 'bg-gray-200 font-semibold text-gray-800'
          : dark
            ? 'text-gray-200 active:bg-zinc-700/60'
            : 'text-gray-700 active:bg-gray-200/70'
      }`,
  };
};

const rowClassName = (palette: Palette): string =>
  `flex w-full min-h-[54px] items-center gap-3.5 border-t px-5 text-left transition-colors ${palette.hairline} ${palette.text}`;

// ---------------------------------------------------------------------------
// Download row

interface DownloadBookRowProps {
  book: Book;
  readerBook: ReaderBook | null;
  voiceKey: string;
  palette: Palette;
}

const DownloadBookRow: React.FC<DownloadBookRowProps> = ({ book, readerBook, voiceKey, palette }) => {
  const { state, canRequest, request, remove } = useBookDownload({ book, readerBook, voiceKey });
  const [mode, setMode] = useState<'row' | 'refused' | 'confirm-remove'>('row');
  const [removing, setRemoving] = useState(false);

  if (mode === 'refused') {
    return (
      <div className={`flex items-center gap-3.5 border-t px-5 py-3.5 ${palette.hairline}`} role="status" data-testid="download-refused">
        <p className={`flex-1 text-sm ${palette.text}`}>{DOWNLOAD_REFUSED_MESSAGE}</p>
        <button type="button" onClick={() => setMode('row')} className={palette.secondaryButton}>
          OK
        </button>
      </div>
    );
  }

  if (mode === 'confirm-remove') {
    const confirmRemove = async () => {
      setRemoving(true);
      try {
        await remove();
      } finally {
        setRemoving(false);
        setMode('row');
      }
    };
    return (
      <div className={`border-t px-5 py-3.5 ${palette.hairline}`} role="group" aria-label="Confirm removing download">
        <p className={`text-sm ${palette.text}`}>{REMOVE_DOWNLOAD_COPY}</p>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={() => setMode('row')} disabled={removing} className={palette.secondaryButton}>
            Keep
          </button>
          <button type="button" onClick={() => void confirmRemove()} disabled={removing} className={palette.dangerButton}>
            {removing ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    );
  }

  const isPinned = state.kind !== 'none';
  const onClick = () => {
    if (isPinned) {
      setMode('confirm-remove');
      return;
    }
    const result = request();
    if (result && !result.pinned) {
      setMode('refused');
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isPinned && !canRequest}
      className={`${rowClassName(palette)} cursor-pointer disabled:cursor-default disabled:opacity-50 ${palette.pressed}`}
      data-testid="download-row"
    >
      <span className={`flex shrink-0 ${palette.accent}`}>
        {state.kind === 'downloaded' ? <TickIcon size="lg" /> : <DownloadIcon size="md" />}
      </span>
      <span className="flex-1 text-[15px] font-medium">{bookDownloadLabel(state)}</span>
      <span className={`text-[13px] ${palette.muted}`} data-testid="download-detail">
        {bookDownloadDetail(state)}
      </span>
      {/* No chevron: this row acts in place. The gap keeps its figure in line with the row above. */}
      <span className="w-4 shrink-0" aria-hidden="true" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// Sheet

export interface PlayerMoreSheetProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  /** After the closing animation, so the player can drop what it set up for the sheet. */
  onExitComplete?: () => void;
  book: Book;
  readerBook: ReaderBook | null;
  voiceKey: string;
  sleepTimer: SleepTimerState | null;
  sleepTimerRemainingSeconds: number | null;
  onSelectSleepMinutes: (minutes: number) => void;
  onSelectSleepEndOfChapter: () => void;
  onCancelSleepTimer: () => void;
}

type SheetBodyProps = Omit<PlayerMoreSheetProps, 'id' | 'isOpen' | 'onExitComplete'> & { palette: Palette };

const SheetBody: React.FC<SheetBodyProps> = ({
  onClose,
  book,
  readerBook,
  voiceKey,
  sleepTimer,
  sleepTimerRemainingSeconds,
  onSelectSleepMinutes,
  onSelectSleepEndOfChapter,
  onCancelSleepTimer,
  palette,
}) => {
  // Lives and dies with the open sheet, so it always opens on the menu.
  const [view, setView] = useState<'menu' | 'sleep-timer'>('menu');
  const sleepValue = sleepTimerRowValue(sleepTimer, sleepTimerRemainingSeconds);

  const bindSwipe = useDrag(({ swipe: [, swipeY] }) => {
    if (swipeY === 1) {
      onClose();
    }
  }, {
    pointer: { touch: true, capture: false },
    swipe: { distance: 20, velocity: 0.2 },
  });

  const choose = (apply: () => void) => () => {
    apply();
    onClose();
  };

  return (
    <div className="mx-auto w-full max-w-152 pt-2 pb-1">
      <div {...bindSwipe()} style={{ touchAction: 'none' }}>
        <div className={`mx-auto h-1 w-10 rounded-full ${palette.grabber}`} aria-hidden="true" />
        <div className="flex items-center justify-between gap-2 py-1 pl-5 pr-2">
          {view === 'menu' ? (
            <p className={`flex min-w-0 items-baseline gap-1.5 text-sm ${palette.text}`} data-testid="more-sheet-book">
              <span className="truncate font-semibold">{book.title}</span>
              <span className={`max-w-[45%] shrink-0 truncate ${palette.muted}`}>· {book.author}</span>
            </p>
          ) : (
            <button
              type="button"
              onClick={() => setView('menu')}
              aria-label="Back to options"
              className={`-ml-2 flex min-w-0 cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 text-sm ${palette.text} ${palette.pressed}`}
            >
              <ChevronIcon direction="left" size="md" />
              <span className="font-semibold">Sleep timer</span>
              {sleepTimer && <span className={palette.muted} role="status">· {sleepValue}</span>}
            </button>
          )}
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full ${palette.muted} ${palette.pressed}`}
          >
            <CloseIcon size="sm" />
          </button>
        </div>
      </div>

      {view === 'menu' ? (
        <>
          <button
            type="button"
            onClick={() => setView('sleep-timer')}
            className={`${rowClassName(palette)} cursor-pointer ${palette.pressed}`}
          >
            <span className={`flex shrink-0 ${palette.accent}`}><ClockIcon size="md" /></span>
            <span className="flex-1 text-[15px] font-medium">Sleep timer</span>
            <span className={`text-[13px] ${palette.muted}`} data-testid="sleep-timer-value">{sleepValue}</span>
            <span className={`flex shrink-0 ${palette.muted}`}><ChevronIcon direction="right" /></span>
          </button>
          <DownloadBookRow book={book} readerBook={readerBook} voiceKey={voiceKey} palette={palette} />
        </>
      ) : (
        <div className={`grid grid-cols-2 gap-1 border-t px-3.5 pt-2 pb-1 ${palette.hairline}`} aria-label="Sleep timer">
          {SLEEP_TIMER_PRESETS_MINUTES.map((minutes) => {
            const active = sleepTimer?.mode === 'countdown' && sleepTimer.minutes === minutes;
            return (
              <button
                key={minutes}
                type="button"
                className={palette.option(active)}
                aria-pressed={active}
                onClick={choose(() => onSelectSleepMinutes(minutes))}
              >
                {minutes} minutes
              </button>
            );
          })}
          <button
            type="button"
            className={`${palette.option(sleepTimer?.mode === 'end-of-chapter')} ${sleepTimer ? '' : 'col-span-2'}`}
            aria-pressed={sleepTimer?.mode === 'end-of-chapter'}
            onClick={choose(onSelectSleepEndOfChapter)}
          >
            End of chapter
          </button>
          {sleepTimer && (
            <button type="button" className={palette.option(false)} onClick={choose(onCancelSleepTimer)}>
              Turn off
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PlayerMoreSheet: React.FC<PlayerMoreSheetProps> = ({ id, isOpen, onExitComplete, ...body }) => {
  const palette = usePalette();
  const reduceMotion = useReducedMotion();
  const sheetRef = useRef<HTMLDivElement>(null);

  // Put the keyboard inside the sheet when it opens.
  useEffect(() => {
    if (isOpen) {
      sheetRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {isOpen && (
        <motion.div
          key="player-more-sheet"
          ref={sheetRef}
          id={id}
          role="dialog"
          aria-label="Player options"
          tabIndex={-1}
          // Anchored to the top edge of the controls and growing upward from
          // it: the panel expands rather than something sliding in over it.
          // The clip keeps the shadow to the top edge: below, the sheet meets
          // the controls with no seam, because they are the same surface.
          className={`absolute inset-x-0 bottom-full z-10 overflow-hidden rounded-t-[22px] shadow-[0_-12px_34px_rgba(14,14,32,0.16)] outline-none [clip-path:inset(-60px_0_0_0)] ${palette.edge}`}
          style={{ backgroundColor: palette.surface }}
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          <SheetBody {...body} palette={palette} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// Scrim

interface PlayerMoreScrimProps {
  isOpen: boolean;
  onClose: () => void;
}

/** The navigation bar's box when it cannot be measured: its row, the safe area above it, and its border. */
const NAVIGATION_FALLBACK_HEIGHT = 'calc(4.25rem + var(--safe-top) + 1px)';

/**
 * How far down the navigation bar reaches, border included, measured while
 * the scrim is open. The reader begins a pixel higher than the bar ends —
 * the bar's bottom border overlaps it — so a piece sized to the bar's row
 * alone leaves that border undimmed: a bright line across a dimmed screen.
 * Sized to the bar's real bottom edge, the two pieces meet exactly, and the
 * part of the reader's piece under the border is hidden by the bar itself.
 */
const useNavigationBottom = (isOpen: boolean): string => {
  const [height, setHeight] = useState(NAVIGATION_FALLBACK_HEIGHT);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    const measure = () => {
      const bottom = document.querySelector('header[role="banner"]')?.getBoundingClientRect().bottom;
      setHeight(bottom && bottom > 0 ? `${bottom}px` : NAVIGATION_FALLBACK_HEIGHT);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  return height;
};

/**
 * Dims everything that is not the panel, and closes the sheet on a tap.
 *
 * Two pieces, because the reader and the top navigation live in different
 * layers: one fills the reader behind the controls, the other is portalled
 * over the navigation bar. Both are pointer conveniences only — the close
 * button and Escape are the accessible ways out — so they are hidden from
 * assistive technology rather than announced as two more buttons.
 */
export const PlayerMoreScrim: React.FC<PlayerMoreScrimProps> = ({ isOpen, onClose }) => {
  const reduceMotion = useReducedMotion();
  const navigationBottom = useNavigationBottom(isOpen);
  const piece = (key: string, className: string, style?: React.CSSProperties) => (
    <motion.div
      key={key}
      aria-hidden="true"
      data-testid={`player-more-scrim-${key}`}
      onClick={onClose}
      className={className}
      style={{ backgroundColor: 'rgba(14, 14, 28, 0.34)', ...style }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.2 }}
    />
  );

  return (
    <>
      <AnimatePresence>{isOpen && piece('reader', 'absolute inset-0 z-40')}</AnimatePresence>
      {createPortal(
        <AnimatePresence>
          {isOpen && piece('navigation', 'fixed inset-x-0 top-0 z-[60]', { height: navigationBottom })}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default PlayerMoreSheet;
